'use client'

import styles from './table-styles.module.css'

import clsx from 'clsx'
import React, { Fragment } from 'react'

import {
  ColumnHelper,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table'
import { Table } from '@wartek-id/table'

import { generateColumns, getHeaderClass } from '@/utils/table'
import { BasicDataRow, NestedColumnType } from '@/apis/types'

interface TableWrapperProps {
  columns: NestedColumnType[]
  data: BasicDataRow[]
  withSummary?: boolean
  onRowClick?: (row: Row<BasicDataRow>, type: 'body' | 'summary') => void
  CustomCellRenderer?: ({ row, col }) => JSX.Element
}

export const NestedTable = ({
  columns = [],
  data,
  withSummary = false,
  onRowClick,
  CustomCellRenderer,
}: TableWrapperProps) => {
  const columnHelper: ColumnHelper<BasicDataRow> =
    createColumnHelper<BasicDataRow>()

  const tableColumns = generateColumns<BasicDataRow>(
    columnHelper,
    columns,
    CustomCellRenderer
  )

  const table = useReactTable<BasicDataRow>({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
  })

  const firstRow = data[0]

  const rows = table.getRowModel().rows

  const rowsBody = withSummary
    ? rows?.filter((row) => !row?.['original']?.['summaryRow'])
    : rows

  const isRowClickable = typeof onRowClick === 'function'

  return (
    <div className="overflow-auto pb-4">
      <Table
        fullWidth
        rounded
        bordered
        variant="striped"
        size="sm"
        className={styles.table}
      >
        <thead className={styles.tableHead}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  className={clsx(
                    getHeaderClass(header.colSpan, firstRow[header.id])
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody
          className={
            withSummary
              ? styles.tableBodyWithFooter
              : styles.tableBodyWithoutFooter
          }
        >
          {rowsBody?.map((row) => (
            <Fragment key={row?.id}>
              <tr
                onClick={() => {
                  if (isRowClickable) {
                    onRowClick?.(row, 'body')
                  }
                }}
                data-row-clickable={isRowClickable}
                className={clsx(isRowClickable ? 'cursor-pointer' : '')}
              >
                {row
                  ?.getVisibleCells()
                  ?.map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
              </tr>
            </Fragment>
          ))}
        </tbody>
      </Table>
    </div>
  )
}
